// Role utility functions for frontend access control

/**
 * Check if user has access to a specific feature based on their role
 * @param {string} userRole - The user's role
 * @param {string|Array} requiredRoles - Required role(s) for access
 * @returns {boolean} - Whether the user has access
 */
export const hasRoleAccess = (userRole, requiredRoles) => {
  if (!userRole || !requiredRoles) return false;
  
  if (Array.isArray(requiredRoles)) {
    return requiredRoles.includes(userRole);
  }
  
  return userRole === requiredRoles;
};

/**
 * Check if user is a partner specialist
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user is a partner specialist
 */
export const isPartnerSpecialist = (userRole) => {
  return userRole === 'partner_specialist';
};

/**
 * Check if user is a manager
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user is a manager
 */
export const isManager = (userRole) => {
  return userRole === 'manager';
};

/**
 * Check if user is an HR user
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user is an HR user
 */
export const isHR = (userRole) => {
  return userRole === 'hr';
};

/**
 * Check if user is a finance user
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user is a finance user
 */
export const isFinance = (userRole) => {
  return userRole === 'finance';
};

/**
 * Check if user is a member (basic user)
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user is a member
 */
export const isMember = (userRole) => {
  return userRole === 'member';
};

/**
 * Check if user can access partner companies
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user can access partner companies
 */
export const canAccessPartnerCompanies = (userRole) => {
  return hasRoleAccess(userRole, ['manager', 'hr', 'partner_specialist']);
};

/**
 * Check if user can access work plan
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user can access work plan
 */
export const canAccessWorkPlan = (userRole) => {
  return hasRoleAccess(userRole, ['member', 'partner_specialist']);
};

/**
 * Get available screens for a specific role
 * @param {string} userRole - The user's role
 * @returns {Array} - Array of available screen identifiers
 */
export const getAvailableScreens = (userRole) => {
  // Finance users should only see finance-specific screens (strict mode)
  if (isFinance(userRole)) {
    return ['finance-attendance', 'finance-payslips', 'finance-payout-summary'];
  }

  const screens = ['dashboard', 'scribble', 'social-wall', 'holiday-calendar', 'leave-management']; // Available for all users
  
  if (canAccessPartnerCompanies(userRole)) {
    screens.push('partner-companies');
  }

  // Hirelane (Hiring) - available for managers and HR
  if (hasRoleAccess(userRole, ['manager', 'hr'])) {
    screens.push('hirelane');
  }
  
  if (isManager(userRole)) {
    screens.push('thehub', 'projects', 'daily-work-plan-review', 'time-offs', 'birdsview', 'peter-conversations', 'task-conversations', 'feature-design-studio');
  } else {
    // Members (non-managers) can access my-projects
    screens.push('my-projects');
  }
  
  if (canAccessWorkPlan(userRole)) {
    screens.push('work-plan');
  }
  
  return screens;
};

 